export const ENGAGEMENT_HEARTBEAT_MS = 30_000
export const ENGAGEMENT_IDLE_MS = 60_000
export const ANALYTICS_SESSION_STORAGE_KEY = 'rafa_analytics_session_id'

export function createAnalyticsId(cryptoObject = globalThis.crypto) {
  if (cryptoObject?.randomUUID) return cryptoObject.randomUUID()

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export function getOrCreateAnalyticsSessionId(storage, createId = createAnalyticsId) {
  try {
    const existingId = storage?.getItem(ANALYTICS_SESSION_STORAGE_KEY)
    if (existingId) return existingId

    const sessionId = createId()
    storage?.setItem(ANALYTICS_SESSION_STORAGE_KEY, sessionId)
    return sessionId
  } catch (error) {
    // sessionStorage can be unavailable in strict privacy modes. A tab-scoped
    // in-memory ID still lets the current document's events be joined.
    return createId()
  }
}

export class PageEngagementTracker {
  constructor({
    pageViewId,
    sessionId,
    path,
    onCheckpoint,
    now = () => performance.now(),
    idleMs = ENGAGEMENT_IDLE_MS,
    initiallyVisible = true,
  }) {
    this.pageViewId = pageViewId
    this.sessionId = sessionId
    this.path = path
    this.onCheckpoint = onCheckpoint
    this.now = now
    this.idleMs = idleMs

    const startedAt = this.now()
    this.lastTickAt = startedAt
    this.lastInteractionAt = startedAt
    this.lastCountedInteractionAt = Number.NEGATIVE_INFINITY
    this.visible = initiallyVisible
    this.visibleMs = 0
    this.engagedMs = 0
    this.interactionCount = 0
    this.checkpointSequence = 0
    this.lastCheckpointSignature = null
  }

  markInteraction() {
    const timestamp = this.now()
    this.accrue(timestamp)
    this.lastInteractionAt = timestamp

    // Scroll and pointer events can arrive in bursts. Count at most one signal
    // per second while still using every signal to refresh the idle deadline.
    if (timestamp - this.lastCountedInteractionAt >= 1000) {
      this.interactionCount += 1
      this.lastCountedInteractionAt = timestamp
    }
  }

  setHidden(hidden) {
    const timestamp = this.now()
    this.accrue(timestamp)
    this.visible = !hidden

    if (hidden) return this.checkpointAt('hidden', timestamp)

    // Returning to the foreground is itself a strong indication of attention.
    this.lastInteractionAt = timestamp
    return null
  }

  checkpoint(reason) {
    const timestamp = this.now()
    this.accrue(timestamp)
    return this.checkpointAt(reason, timestamp)
  }

  accrue(timestamp) {
    if (timestamp <= this.lastTickAt) return

    if (this.visible) {
      this.visibleMs += timestamp - this.lastTickAt
      const engagedUntil = Math.min(timestamp, this.lastInteractionAt + this.idleMs)
      this.engagedMs += Math.max(0, engagedUntil - this.lastTickAt)
    }

    this.lastTickAt = timestamp
  }

  checkpointAt(reason, timestamp) {
    const visibleMs = Math.round(this.visibleMs)
    const engagedMs = Math.min(visibleMs, Math.round(this.engagedMs))
    // Once the idle threshold has elapsed, visible time can keep increasing for
    // hours without adding useful engagement information. Heartbeats therefore
    // deduplicate on engaged time; lifecycle checkpoints still preserve the
    // final foreground total.
    const signatureParts = reason === 'heartbeat'
      ? ['heartbeat', engagedMs, this.interactionCount, this.visible]
      : ['lifecycle', visibleMs, engagedMs, this.interactionCount, this.visible]
    const signature = signatureParts.join(':')
    if (signature === this.lastCheckpointSignature) return null

    this.lastCheckpointSignature = signature
    this.checkpointSequence += 1
    const payload = {
      page_view_id: this.pageViewId,
      session_id: this.sessionId,
      path: this.path,
      visible_ms: visibleMs,
      engaged_ms: engagedMs,
      interaction_count: this.interactionCount,
      checkpoint_sequence: this.checkpointSequence,
      reason,
    }
    this.onCheckpoint(payload, timestamp)
    return payload
  }
}
